import { useEffect, useState } from 'react'
import { gifId, gifLink, giphyFetch, giphyMedia, useGiphyConfig, type Gif } from '@/api/giphy'
import { Button, Modal, TextField } from './ui'
import { useExternalImages } from '@/lib/externalImages'
import styles from './Giphy.module.css'

function Attribution() { return <img className={styles.attribution} src="/powered-by-giphy.png" alt="Powered by GIPHY" width={160} /> }
export function GiphyButton({ onOpen }: { onOpen: () => void }) {
  const config = useGiphyConfig()
  return config.data?.enabled ? <Button size="sm" variant="ghost" onClick={onOpen}>GIF</Button> : null
}
export function GiphyPicker({ onSelect, onClose }: { onSelect: (link: string) => void; onClose: () => void }) {
  const config = useGiphyConfig()
  const [query, setQuery] = useState(''), [items, setItems] = useState<Gif[]>([]), [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(true)
  const [selected, setSelected] = useState<Gif | null>(null)
  useEffect(() => {
    if (!config.data) return
    const controller = new AbortController()
    setBusy(true); setItems([]); setError(null)
    const timer = setTimeout(() => { void giphyFetch(config.data!, query, controller.signal).then(setItems).catch(error => { if (!controller.signal.aborted) setError((error as Error).message) }).finally(() => { if (!controller.signal.aborted) setBusy(false) }) }, 350)
    return () => { clearTimeout(timer); controller.abort() }
  }, [config.data, query])
  const selectedUrl = selected && giphyMedia(selected.images.original.url)
  return <Modal open onClose={onClose} title="Choose a GIF"><div className={styles.picker}>
    <Attribution /><p>Search and images load from GIPHY, which receives your search and IP address.</p>
    <TextField label="Search GIFs" maxLength={100} value={query} onChange={event => { setQuery(event.target.value); setSelected(null) }} />
    {config.error || error ? <p role="alert">{error ?? 'Could not load GIPHY settings.'}</p> : busy ? <p role="status">Loading GIFs…</p> : null}
    {selected && selectedUrl ? <div><img className={styles.preview} src={selectedUrl} alt={selected.title} referrerPolicy="no-referrer" />
      <Button variant="primary" onClick={() => onSelect(gifLink(selected.id))}>Insert GIF</Button><Button onClick={() => setSelected(null)}>Back to results</Button></div> : <div className={styles.grid}>
      {items.map(item => { const url = giphyMedia(item.images?.fixed_height?.url); return <button type="button" key={item.id} disabled={!url} aria-label={item.title || 'Choose GIF'} onClick={() => setSelected(item)}>
        {url ? <img src={url} alt={item.title} referrerPolicy="no-referrer" loading="lazy" /> : <span>GIF unavailable</span>}</button> })}
    </div>}
    {!busy && !error && items.length === 0 ? <p>No GIFs found.</p> : null}
  </div></Modal>
}

/** Only the canonical ID link is persisted in the message; renditions live in this mounted view. */
export function GiphyMessage({ text }: { text: string }) {
  const id = gifId(text)
  const allowExternal = useExternalImages()
  const [attempt, setAttempt] = useState(0)
  const approved = attempt > 0
  const config = useGiphyConfig()
  const [gif, setGif] = useState<Gif | null>(null), [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!id || (!approved && !allowExternal) || !config.data) return
    const controller = new AbortController()
    setError(null)
    void giphyFetch(config.data, '', controller.signal, id).then(items => setGif(items[0] ?? null)).catch(error => { if (!controller.signal.aborted) setError((error as Error).message) })
    return () => controller.abort()
  }, [id, approved, attempt, allowExternal, config.data])
  if (!id) return null
  const url = gif && giphyMedia(gif.images?.original?.url)
  return <div className={styles.message}>
    {url ? <img className={styles.preview} src={url} alt={gif?.title ?? 'GIF'} referrerPolicy="no-referrer" /> : <Button size="sm" onClick={() => setAttempt(value => value + 1)}>Load GIF from GIPHY</Button>}
    {error ? <p>{error}</p> : null}
    {!approved && !allowExternal ? <small>Loading shares your IP address with GIPHY.</small> : null}
    <a href={gifLink(id)} target="_blank" rel="noopener noreferrer">Open GIF</a><Attribution />
  </div>
}
